import type { AdvancedEditCut } from '../../../../../domain';

export type TransitionType = 'jump_cut' | 'crossfade';

export type EditedSegment = {
  id: string;
  recordingId: string;
  originalStartTime: number;
  originalEndTime: number;
  outputStartTime: number;
  outputEndTime: number;
  isDeleted: boolean;
  transitionIn?: TransitionType;
};

export type ComputedJoint = {
  id: string;
  beforeSegmentId: string | null;
  afterSegmentId: string | null;
  outputPosition: number;
  transition: TransitionType;
  deletedSegments: EditedSegment[];
  deletedDuration: number;
};

const DELETED_OUTPUT_TIME = -1;

const mapTransition = (cut: AdvancedEditCut): TransitionType =>
  cut.transition?.type === 'fade' ? 'crossfade' : 'jump_cut';

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

const makeDeletedSegmentId = (
  recordingId: string,
  startMs: number,
  endMs: number,
  index: number,
): string => `deleted-${recordingId}-${startMs}-${endMs}-${index}`;

const toDurationMs = (start: number, end: number): number =>
  Math.max(0, end - start);

const deletedDuration = (segments: EditedSegment[]): number =>
  segments.reduce(
    (total, segment) =>
      total + toDurationMs(segment.originalStartTime, segment.originalEndTime),
    0,
  );

/**
 * Builds POC-style timeline segments from active cuts.
 * Deleted segments are inferred from gaps between cuts.
 */
export const cutsToSegments = (
  cuts: AdvancedEditCut[],
  recordingId: string,
  recordingDurationSec: number,
): EditedSegment[] => {
  if (cuts.length === 0) {
    return [];
  }

  const recordingDurationMs = Math.max(
    0,
    Math.round(recordingDurationSec * 1000),
  );
  const orderedCuts = [...cuts].sort(
    (left, right) => left.start_time_ms - right.start_time_ms,
  );

  const segments: EditedSegment[] = [];
  let previousEndMs = 0;
  let outputCursorMs = 0;

  for (let index = 0; index < orderedCuts.length; index += 1) {
    const cut = orderedCuts[index];
    const startMs = clamp(cut.start_time_ms, 0, recordingDurationMs);
    const endMs = clamp(cut.end_time_ms, 0, recordingDurationMs);

    if (endMs <= startMs || startMs < previousEndMs) {
      continue;
    }

    if (startMs > previousEndMs) {
      segments.push({
        id: makeDeletedSegmentId(recordingId, previousEndMs, startMs, index),
        recordingId,
        originalStartTime: previousEndMs,
        originalEndTime: startMs,
        outputStartTime: DELETED_OUTPUT_TIME,
        outputEndTime: DELETED_OUTPUT_TIME,
        isDeleted: true,
      });
    }

    const cutDuration = toDurationMs(startMs, endMs);
    segments.push({
      id: cut.id,
      recordingId: cut.recording_id,
      originalStartTime: startMs,
      originalEndTime: endMs,
      outputStartTime: outputCursorMs,
      outputEndTime: outputCursorMs + cutDuration,
      isDeleted: false,
      transitionIn: mapTransition(cut),
    });

    outputCursorMs += cutDuration;
    previousEndMs = endMs;
  }

  if (previousEndMs < recordingDurationMs) {
    segments.push({
      id: makeDeletedSegmentId(
        recordingId,
        previousEndMs,
        recordingDurationMs,
        orderedCuts.length,
      ),
      recordingId,
      originalStartTime: previousEndMs,
      originalEndTime: recordingDurationMs,
      outputStartTime: DELETED_OUTPUT_TIME,
      outputEndTime: DELETED_OUTPUT_TIME,
      isDeleted: true,
    });
  }

  return segments;
};

/**
 * Computes joint metadata expected by POC timeline components.
 */
export const computeJointsFromSegments = (
  segments: EditedSegment[],
  originalRecordingId: string,
): ComputedJoint[] => {
  if (segments.length === 0) {
    return [];
  }

  const activeIndexes = segments
    .map((segment, index) => ({ segment, index }))
    .filter(({ segment }) => !segment.isDeleted);

  if (activeIndexes.length === 0) {
    return [];
  }

  const joints: ComputedJoint[] = [];
  const firstActive = activeIndexes[0];
  const leadingDeleted = segments
    .slice(0, firstActive.index)
    .filter(
      segment =>
        segment.isDeleted && segment.recordingId === originalRecordingId,
    );

  joints.push({
    id: `joint-start-${firstActive.segment.id}`,
    beforeSegmentId: null,
    afterSegmentId: firstActive.segment.id,
    outputPosition: 0,
    transition: firstActive.segment.transitionIn ?? 'jump_cut',
    deletedSegments: leadingDeleted,
    deletedDuration: deletedDuration(leadingDeleted),
  });

  for (let index = 0; index < activeIndexes.length - 1; index += 1) {
    const current = activeIndexes[index];
    const next = activeIndexes[index + 1];
    const betweenDeleted = segments
      .slice(current.index + 1, next.index)
      .filter(
        segment =>
          segment.isDeleted && segment.recordingId === originalRecordingId,
      );

    joints.push({
      id: `joint-${current.segment.id}-${next.segment.id}`,
      beforeSegmentId: current.segment.id,
      afterSegmentId: next.segment.id,
      outputPosition: current.segment.outputEndTime,
      transition: next.segment.transitionIn ?? 'jump_cut',
      deletedSegments: betweenDeleted,
      deletedDuration: deletedDuration(betweenDeleted),
    });
  }

  const lastActive = activeIndexes[activeIndexes.length - 1];
  const trailingDeleted = segments
    .slice(lastActive.index + 1)
    .filter(
      segment =>
        segment.isDeleted && segment.recordingId === originalRecordingId,
    );

  joints.push({
    id: `joint-${lastActive.segment.id}-end`,
    beforeSegmentId: lastActive.segment.id,
    afterSegmentId: null,
    outputPosition: lastActive.segment.outputEndTime,
    transition: 'jump_cut',
    deletedSegments: trailingDeleted,
    deletedDuration: deletedDuration(trailingDeleted),
  });

  return joints;
};

/**
 * Maps an output (edited timeline) time in seconds to its corresponding
 * source-recording time in milliseconds. Returns the end of the last active
 * segment when the output time is beyond the edited duration, or `null` when
 * there are no active segments.
 */
export const mapOutputToSourceMs = (
  segments: EditedSegment[],
  outputTimeSec: number,
): number | null => {
  const outputTimeMs = outputTimeSec * 1000;
  const activeSegments = segments.filter(segment => !segment.isDeleted);

  for (const segment of activeSegments) {
    if (
      outputTimeMs >= segment.outputStartTime &&
      outputTimeMs <= segment.outputEndTime
    ) {
      return (
        segment.originalStartTime + (outputTimeMs - segment.outputStartTime)
      );
    }
  }

  const lastSegment = activeSegments.at(-1);
  return lastSegment ? lastSegment.originalEndTime : null;
};
